import { Checkbox, FormCard, FormItem, TextArea } from '@lmc/ui-kit'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Form as AntdForm, Select, FormInstance } from 'antd'
import { Heading, Text } from '@lmc/ui-kit/src/general'
import { incidentReportsConstants } from 'locale/incidentReports'
import { getNominative } from 'common/utils'
import {
  FollowUpActionStatus,
  Home,
  IncidentSeverityValues,
} from '@lmc/ts-common'
import { useSelector } from 'react-redux'
import { actionPlanValidator } from 'screens/Residents/screens/ResidentsGoalsScreen/components/GoalForm/GoalForm'
import useResidentsList from 'hooks/useResidentsList'
import ActionsInput from 'screens/Residents/screens/ResidentsGoalsScreen/components/ActionPlanInput/ActionPlanInput'
import useBodyMaps from 'hooks/useBodyMaps'
import UploadAttachmentsOnSaveInput from '../UploadAttachmentsOnSaveInput'
import { getSeverityOptions } from '../../utils/incidentSeverityUtils'
import { useCarers } from 'screens/Organisation/screens/TeamList'

type IncidentCaseFormFieldsProps = {
  form: FormInstance
  severities: IncidentSeverityValues | undefined
  residentId: string
}

export const followUpActionsFieldName = ['followUpActions', 'actions']

export const IncidentCaseFormFields = ({
  form,
  severities,
  residentId,
}: IncidentCaseFormFieldsProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const currentHome: Home = useSelector(
    (state: { global: { home: Home } }) => state.global.home
  )
  const preferredPeopleNominative: string =
    currentHome.preferredPeopleNominative || ''
  const currentHomeId: string = currentHome.id

  const { residents } = useResidentsList({})

  const { bodyMaps } = useBodyMaps({ residentId })

  const { carers } = useCarers()

  const serviceUsersOptions = residents
    ?.filter(
      (resident) =>
        resident.name.first !== undefined &&
        resident.name.last !== undefined &&
        resident.id !== residentId
    )
    .map((carer) => ({
      label: `${carer.name.first} ${carer.name.last}`,
      value: carer.id,
    }))
  const carerOptions = carers
    ?.filter(
      (carerPerson) =>
        carerPerson.name.first !== undefined &&
        carerPerson.name.last !== undefined &&
        carerPerson.active &&
        (carerPerson.home === currentHomeId ||
          carerPerson.homes.includes(currentHomeId))
    )
    .map((carer) => ({
      label: `${carer.name.first} ${carer.name.last}`,
      value: carer.id,
    }))

  const bodyMapOptions = bodyMaps?.map((bodyMap) => ({
    value: bodyMap.id,
    label: bodyMap.title,
  }))
  const physicalIntervention = AntdForm.useWatch(
    (values) => values.physicalIntervention?.hasBeenApplied,
    form
  )
  const restraints = AntdForm.useWatch(
    (values) => values.restraints?.hasBeenApplied,
    form
  )
  const chemicalRestraints = AntdForm.useWatch(
    (values) => values.chemicalRestraints?.hasBeenApplied,
    form
  )
  const residentInjuries = AntdForm.useWatch(
    (values) => values.residentInjuries?.hasBeenApplied,
    form
  )
  const carersInjuries = AntdForm.useWatch(
    (values) => values.carersInjuries?.hasBeenApplied,
    form
  )
  const otherPartiesInjuries = AntdForm.useWatch(
    (values) => values.otherPartiesInjuries?.hasBeenApplied,
    form
  )
  const damages = AntdForm.useWatch(
    (values) => values.damages?.hasBeenApplied,
    form
  )

  const prnMedication = AntdForm.useWatch(
    (values) => values.prnMedication?.hasBeenApplied,
    form
  )

  const filterOption = (input: string, option?: { label: string }) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const severityOptions = getSeverityOptions(severities, t)
  const actionInputOptions = Object.keys(FollowUpActionStatus).map(
    (status) => ({
      label: t(
        incidentReportsConstants[
          FollowUpActionStatus[status as FollowUpActionStatus]
        ]
      ),
      value: FollowUpActionStatus[status as FollowUpActionStatus],
    })
  )

  return (
    <Wrapper>
      <Title level={2}>{t(incidentReportsConstants.INCIDENT_CASE)}</Title>
      <FormCard title={t(incidentReportsConstants.INCIDENT_DETAILS)}>
        <FormItem
          label={<Text>{t(incidentReportsConstants.SEVERITY)}</Text>}
          name="severity"
        >
          <Select options={severityOptions} />
        </FormItem>
        <FormItem
          label={
            <Text>
              {t(incidentReportsConstants.OTHER_SERVICE_USERS_INVOLVED, {
                variable: getNominative(preferredPeopleNominative, 'residents'),
              })}
            </Text>
          }
          name={['serviceUsersInvolved']}
        >
          <Select
            mode="multiple"
            allowClear
            options={serviceUsersOptions}
            filterOption={filterOption}
          />
        </FormItem>
        <FormItem
          label={
            <Text>{t(incidentReportsConstants.OTHER_PEOPLE_INVOLVED)}</Text>
          }
          name={['otherPeopleInvolved']}
        >
          <TextArea
            autoSize={{
              minRows: 3,
            }}
          />
        </FormItem>
        <FormItem
          label={<Text>{t(incidentReportsConstants.OTHER_WITNESS)}</Text>}
          name={['otherWitnesses']}
        >
          <TextArea
            autoSize={{
              minRows: 3,
            }}
          />
        </FormItem>
        <FormItem
          label={<Text>{t(incidentReportsConstants.CARERES_INVOLVED)}</Text>}
          name={['carersInvolved']}
        >
          <Select mode="multiple" allowClear options={carerOptions} />
        </FormItem>
        <FormCard
          title={t(incidentReportsConstants.RESTRICTIVE_PRACTICES)}
          level={2}
        >
          <FormItem
            valuePropName="checked"
            name={['physicalIntervention', 'hasBeenApplied']}
          >
            <Checkbox>
              <Text>
                {t(incidentReportsConstants.PHYSICAL_INTERVENTION_APPLIED)}
              </Text>
            </Checkbox>
          </FormItem>
          {physicalIntervention && (
            <FormItem
              name={['physicalIntervention', 'details']}
              label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
            >
              <TextArea
                autoSize={{
                  minRows: 3,
                }}
              />
            </FormItem>
          )}
          <FormItem
            valuePropName="checked"
            name={['restraints', 'hasBeenApplied']}
          >
            <Checkbox>
              <Text>{t(incidentReportsConstants.RESTRAINTS_APPLIED)}</Text>
            </Checkbox>
          </FormItem>
          {restraints && (
            <FormItem
              name={['restraints', 'details']}
              label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
            >
              <TextArea
                autoSize={{
                  minRows: 3,
                }}
              />
            </FormItem>
          )}
          <FormItem
            valuePropName="checked"
            name={['chemicalRestraints', 'hasBeenApplied']}
          >
            <Checkbox>
              <Text>
                {t(incidentReportsConstants.CHEMICAL_RESTRAINTS_APPLIED)}
              </Text>
            </Checkbox>
          </FormItem>
          {chemicalRestraints && (
            <FormItem
              name={['chemicalRestraints', 'details']}
              label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
            >
              <TextArea
                autoSize={{
                  minRows: 3,
                }}
              />
            </FormItem>
          )}
        </FormCard>
        <FormCard title={t(incidentReportsConstants.INJURIES)} level={2}>
          <FormItem
            valuePropName="checked"
            name={['residentInjuries', 'hasBeenApplied']}
          >
            <Checkbox>
              <Text>
                {t(incidentReportsConstants.INJURIES_OF_RESIDENT, {
                  variable: getNominative(preferredPeopleNominative),
                })}
              </Text>
            </Checkbox>
          </FormItem>
          {residentInjuries && (
            <FormItem
              name={['residentInjuries', 'details']}
              label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
            >
              <TextArea
                autoSize={{
                  minRows: 3,
                }}
              />
            </FormItem>
          )}
          {residentInjuries && (
            <FormItem
              name={['residentInjuries', 'residentInjuriesBodyMap']}
              label={<Text>{t(incidentReportsConstants.RELATED_BODY)}</Text>}
              tooltip={t(incidentReportsConstants.YOU_CAN_SELECT)}
            >
              <Select
                optionRender={(option) => <Text>{option.label}</Text>}
                options={bodyMapOptions}
                allowClear
              />
            </FormItem>
          )}
          <FormItem
            valuePropName="checked"
            name={['carersInjuries', 'hasBeenApplied']}
          >
            <Checkbox>
              <Text>{t(incidentReportsConstants.INJURIES_OF_CARERS)}</Text>
            </Checkbox>
          </FormItem>
          {carersInjuries && (
            <FormItem
              name={['carersInjuries', 'details']}
              label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
            >
              <TextArea
                autoSize={{
                  minRows: 3,
                }}
              />
            </FormItem>
          )}
          <FormItem
            valuePropName="checked"
            name={['otherPartiesInjuries', 'hasBeenApplied']}
          >
            <Checkbox>
              <Text>
                {t(incidentReportsConstants.INJURIES_OF_OTHER_PARTIES)}
              </Text>
            </Checkbox>
          </FormItem>
          {otherPartiesInjuries && (
            <FormItem
              name={['otherPartiesInjuries', 'details']}
              label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
            >
              <TextArea
                autoSize={{
                  minRows: 3,
                }}
              />
            </FormItem>
          )}
        </FormCard>
        <FormCard title={t(incidentReportsConstants.DAMAGES)} level={2}>
          <FormItem
            valuePropName="checked"
            name={['damages', 'hasBeenApplied']}
          >
            <Checkbox>
              <Text>{t(incidentReportsConstants.PROPERTIES_OR_ENV)}</Text>
            </Checkbox>
          </FormItem>
          {damages && (
            <FormItem
              name={['damages', 'details']}
              label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
            >
              <TextArea
                autoSize={{
                  minRows: 3,
                }}
              />
            </FormItem>
          )}
        </FormCard>
        <FormCard title={t(incidentReportsConstants.MEDICATIONS)} level={2}>
          <FormItem
            valuePropName="checked"
            name={['prnMedication', 'hasBeenApplied']}
          >
            <Checkbox>
              <Text>{t(incidentReportsConstants.PRN_MEDICATIONS)}</Text>
            </Checkbox>
          </FormItem>
          {prnMedication && (
            <FormItem
              name={['prnMedication', 'details']}
              label={<Text>{t(incidentReportsConstants.DETAILS)}</Text>}
            >
              <TextArea
                autoSize={{
                  minRows: 3,
                }}
              />
            </FormItem>
          )}
        </FormCard>
        <FormCard
          title={t(incidentReportsConstants.SU_OPINION, {
            variable: getNominative(preferredPeopleNominative),
          })}
          level={2}
        >
          <FormItem name={['serviceUserOpinion']}>
            <TextArea
              autoSize={{
                minRows: 3,
              }}
            />
          </FormItem>
        </FormCard>
        <FormCard
          title={t(incidentReportsConstants.ADDITIONAL_COMMENT)}
          level={2}
        >
          <FormItem name={['incidentOtherInformation']}>
            <TextArea
              autoSize={{
                minRows: 3,
              }}
            />
          </FormItem>
        </FormCard>
        <div>
          <FormItem
            label={<Text>{t(incidentReportsConstants.ATTACHMENTS)}</Text>}
            name={['attachments']}
          >
            <UploadAttachmentsOnSaveInput />
          </FormItem>
        </div>
      </FormCard>
      <FormCard title={t(incidentReportsConstants.FOLLOW_UP_ACTIONS)}>
        <FormItem
          valuePropName="checked"
          name={['followUpActions', 'safeguardingActionsRequired']}
        >
          <Checkbox>
            <Text>{t(incidentReportsConstants.SAFEGUARDING)}</Text>
          </Checkbox>
        </FormItem>
        <FormItem
          valuePropName="checked"
          name={['followUpActions', 'notificationRequired']}
        >
          <Checkbox>
            <Text>{t(incidentReportsConstants.REGULATOR_NOTIFICATION)}</Text>
          </Checkbox>
        </FormItem>
        <FormItem
          name={followUpActionsFieldName}
          rules={[
            {
              validator: (rule, value) => actionPlanValidator(value, t),
            },
          ]}
        >
          <ActionsInput
            options={actionInputOptions}
            defaultStatusValue={FollowUpActionStatus.notStarted}
            buttonText={t(incidentReportsConstants.ADD_ACTION)}
          />
        </FormItem>
      </FormCard>
      <FormCard title={t(incidentReportsConstants.DEBRIEFING)}>
        <FormItem
          label={<Text>{t(incidentReportsConstants.NOTES)}</Text>}
          name={['debriefingNotes']}
        >
          <TextArea
            autoSize={{
              minRows: 3,
            }}
          />
        </FormItem>
      </FormCard>
      <FormCard title={t(incidentReportsConstants.LEARNING_OUTCOMES)}>
        <FormItem
          label={<Text>{t(incidentReportsConstants.ROOT_CAUSE)}</Text>}
          name={['rootCause']}
        >
          <TextArea
            autoSize={{
              minRows: 3,
            }}
          />
        </FormItem>
        <FormItem
          label={<Text>{t(incidentReportsConstants.ACTIONS_TAKEN)}</Text>}
          name={['preventativeActions']}
        >
          <TextArea
            autoSize={{
              minRows: 3,
            }}
          />
        </FormItem>
        <FormItem
          label={<Text>{t(incidentReportsConstants.OTHER_NOTES)}</Text>}
          name={['otherNotes']}
        >
          <TextArea
            autoSize={{
              minRows: 3,
            }}
          />
        </FormItem>
      </FormCard>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding-bottom: 64px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const Title = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

export default IncidentCaseFormFields
